import { useCallback, useEffect, useRef, useState } from 'react';
import { FaMapMarkerAlt, FaSpinner, FaTimes } from 'react-icons/fa';
import { parseGoogleAddress } from '@shared-utils/addressUtils';

/**
 * Google Places address autocomplete — the same lookup the public site uses in
 * `marketplace/app/Hero.tsx`, so staff search an address the way a customer does
 * instead of hand-typing seven Vocus components.
 *
 * Emits `onSelect({ components, formattedAddress })` where `components` is the
 * Vocus-normalised shape from `parseGoogleAddress` (uppercase, street type
 * abbreviated, `city` aliased to suburb) — ready to post as `addressComponents`.
 *
 * Needs `VITE_GOOGLE_MAPS_API_KEY` at build time. Without it the component
 * renders a disabled input and reports `unavailable` so the caller can fall back
 * to the manual component form.
 */

const API_KEY = (import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '').trim();
const SCRIPT_ID = 'peaknet-admin-google-maps';

// Module-level so a remount (tab switch) doesn't re-inject the script tag.
let loaderPromise = null;

function loadPlaces() {
  if (!API_KEY) return Promise.reject(new Error('no-api-key'));
  if (loaderPromise) return loaderPromise;

  loaderPromise = new Promise((resolve, reject) => {
    if (window.google?.maps?.places) { resolve(); return; }

    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('script-error')));
      return;
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.async = true;
    script.defer = true;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(API_KEY)}&libraries=places&v=weekly`;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('script-error'));
    document.head.appendChild(script);
  }).then(async () => {
    // v=weekly loads `places` lazily; importLibrary settles it before first use.
    try { await window.google?.maps?.importLibrary?.('places'); } catch { /* legacy path still works */ }
    if (!window.google?.maps?.places) throw new Error('places-unavailable');
  });

  return loaderPromise;
}

export default function AddressAutocomplete({
  value,
  onChange,
  onSelect,
  disabled = false,
  placeholder = 'Start typing an address — e.g. 1159 Sandgate Road, Nundah QLD',
  autoFocus = false,
}) {
  const [predictions, setPredictions] = useState([]);
  const [open, setOpen] = useState(false);
  const [ready, setReady] = useState(false);
  const [mode, setMode] = useState('off');      // 'off' | 'legacy' | 'new'
  const [loadError, setLoadError] = useState(API_KEY ? null : 'no-api-key');
  const [resolving, setResolving] = useState(false);
  const [highlight, setHighlight] = useState(-1);

  const sessionTokenRef = useRef(null);
  const reqIdRef = useRef(0);
  const boxRef = useRef(null);

  // Load on first interaction rather than on mount: the availability tool is one
  // tab among many and most admin sessions never open it.
  const [requested, setRequested] = useState(false);

  useEffect(() => {
    if (!requested || ready || loadError === 'no-api-key') return;
    let cancelled = false;
    loadPlaces()
      .then(() => {
        if (cancelled) return;
        const places = window.google?.maps?.places;
        // Prefer the legacy AutocompleteService: it goes to maps.googleapis.com on the
        // same key the public site uses. The new AutocompleteSuggestion RPC
        // (places.googleapis.com) needs separate API enablement and 400s without it.
        const hasLegacy = typeof places?.AutocompleteService === 'function';
        const hasNew = typeof places?.AutocompleteSuggestion?.fetchAutocompleteSuggestions === 'function';
        if (!hasLegacy && !hasNew) { setLoadError('places-unavailable'); return; }
        const TokenCtor = places?.AutocompleteSessionToken;
        if (typeof TokenCtor === 'function') sessionTokenRef.current = new TokenCtor();
        setMode(hasLegacy ? 'legacy' : 'new');
        setReady(true);
      })
      .catch((err) => { if (!cancelled) setLoadError(err?.message || 'load-failed'); });
    return () => { cancelled = true; };
  }, [requested, ready, loadError]);

  // Close on outside click.
  useEffect(() => {
    const onDocDown = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, []);

  const fetchPredictions = useCallback((input) => {
    if (!ready || mode === 'off' || input.trim().length < 3) {
      setPredictions([]);
      setOpen(false);
      return;
    }
    const reqId = ++reqIdRef.current;
    const places = window.google?.maps?.places;

    if (mode === 'new') {
      void (async () => {
        try {
          const { suggestions } = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
            input,
            sessionToken: sessionTokenRef.current ?? undefined,
            // CLDR region codes are uppercase; lowercase "au" 400s from the AutocompletePlaces RPC.
            includedRegionCodes: ['AU'],
            region: 'AU',
          });
          if (reqId !== reqIdRef.current) return;
          const list = (suggestions || [])
            .map((s) => s.placePrediction)
            .filter(Boolean)
            .map((pp) => ({
              placeId: pp.placeId,
              description: pp.text?.text ?? '',
              mainText: pp.mainText?.text,
              secondaryText: pp.secondaryText?.text,
              newPrediction: pp,
            }));
          setPredictions(list);
          setHighlight(-1);
          setOpen(true);
        } catch {
          if (reqId !== reqIdRef.current) return;
          setPredictions([]);
          setOpen(false);
        }
      })();
      return;
    }

    const service = new places.AutocompleteService();
    service.getPlacePredictions(
      {
        input,
        componentRestrictions: { country: 'au' },
        types: ['address'],
        sessionToken: sessionTokenRef.current ?? undefined,
      },
      (results, status) => {
        if (reqId !== reqIdRef.current) return;
        if (status === 'OK' && results) {
          setPredictions(results.map((r) => ({
            placeId: r.place_id,
            description: r.description,
            mainText: r.structured_formatting?.main_text,
            secondaryText: r.structured_formatting?.secondary_text,
          })));
          setHighlight(-1);
          setOpen(true);
        } else {
          setPredictions([]);
          setOpen(false);
        }
      }
    );
  }, [ready, mode]);

  const handleChange = (e) => {
    const v = e.target.value;
    onChange?.(v);
    if (!requested) setRequested(true);
    fetchPredictions(v);
  };

  /**
   * Resolve a prediction to Vocus components. The description alone already
   * parses (regex fallback in parseGoogleAddress), so we set that first and
   * upgrade to Google's structured address_components when the details call
   * returns — a slow Places response never blocks the staff member.
   */
  const choose = async (p) => {
    setOpen(false);
    setPredictions([]);

    let components = parseGoogleAddress([], p.description);
    let formatted = components.fullAddress || p.description;
    onChange?.(formatted);

    setResolving(true);
    try {
      const applyPlace = async (place) => {
        await place.fetchFields({ fields: ['addressComponents', 'formattedAddress'] });
        if (!place.addressComponents?.length) return;
        const legacyShape = place.addressComponents.map((c) => ({
          long_name: c.longText,
          short_name: c.shortText,
          types: c.types,
        }));
        components = parseGoogleAddress(legacyShape, place.formattedAddress || p.description);
        formatted = components.fullAddress || place.formattedAddress || p.description;
        onChange?.(formatted);
      };

      if (p.newPrediction) {
        await applyPlace(p.newPrediction.toPlace());
      } else if (window.google?.maps?.importLibrary) {
        const lib = await window.google.maps.importLibrary('places');
        if (lib?.Place) await applyPlace(new lib.Place({ id: p.placeId }));
      }
    } catch {
      /* keep the description-derived parse */
    } finally {
      setResolving(false);
      // A session token covers one autocomplete-then-details cycle; rotate it so
      // the next search is billed as a new session.
      const TokenCtor = window.google?.maps?.places?.AutocompleteSessionToken;
      if (typeof TokenCtor === 'function') sessionTokenRef.current = new TokenCtor();
    }

    onSelect?.({ components, formattedAddress: formatted });
  };

  const onKeyDown = (e) => {
    if (!open || predictions.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight((h) => (h + 1) % predictions.length); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight((h) => (h <= 0 ? predictions.length - 1 : h - 1)); }
    else if (e.key === 'Enter' && highlight >= 0) { e.preventDefault(); void choose(predictions[highlight]); }
    else if (e.key === 'Escape') { setOpen(false); }
  };

  const unavailable = loadError != null;

  return (
    <div ref={boxRef} className="relative">
      <div className="relative">
        <FaMapMarkerAlt className="absolute left-3 top-1/2 -translate-y-1/2 text-[#83a598] pointer-events-none" />
        <input
          type="text"
          value={value}
          onChange={handleChange}
          onFocus={() => { if (!requested) setRequested(true); if (predictions.length) setOpen(true); }}
          onKeyDown={onKeyDown}
          disabled={disabled || unavailable}
          autoFocus={autoFocus}
          autoComplete="off"
          placeholder={unavailable ? 'Address autocomplete unavailable — use the manual fields below' : placeholder}
          className="w-full pl-10 pr-10 py-3 bg-[#1d2021] border border-[#3c3836] rounded-lg text-[#ebdbb2] placeholder-[#665c54] focus:outline-none focus:border-[#fabd2f] disabled:opacity-60"
        />
        {resolving && <FaSpinner className="absolute right-3 top-1/2 -translate-y-1/2 text-[#fabd2f] animate-spin" />}
        {!resolving && value && !unavailable && (
          <button
            type="button"
            onClick={() => { onChange?.(''); setPredictions([]); setOpen(false); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#928374] hover:text-[#ebdbb2]"
            aria-label="Clear address"
          >
            <FaTimes />
          </button>
        )}
      </div>

      {unavailable && (
        <p className="mt-1.5 text-[11px] text-[#d79921]">
          {loadError === 'no-api-key'
            ? 'VITE_GOOGLE_MAPS_API_KEY is not set for this build — autocomplete is off.'
            : 'Google Places failed to load — check the key\'s referrer restrictions allow admin.peaknet.com.au.'}
          {' '}Enter the address manually below.
        </p>
      )}

      {open && predictions.length > 0 && (
        <ul className="absolute z-50 left-0 right-0 mt-2 bg-[#282828] border border-[#504945] rounded-lg shadow-2xl max-h-80 overflow-y-auto">
          {predictions.map((p, i) => (
            <li key={p.placeId || p.description}>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); void choose(p); }}
                onMouseEnter={() => setHighlight(i)}
                className={`w-full text-left px-4 py-2.5 flex items-start gap-3 border-b border-[#3c3836] last:border-b-0 ${
                  i === highlight ? 'bg-[#fabd2f]/15' : 'hover:bg-[#3c3836]'
                }`}
              >
                <FaMapMarkerAlt className="mt-1 shrink-0 text-[#928374]" />
                <span className="min-w-0">
                  <span className="block text-sm text-[#ebdbb2] truncate">{p.mainText || p.description}</span>
                  {p.secondaryText && <span className="block text-[11px] text-[#a89984] truncate">{p.secondaryText}</span>}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
